import { API_BASE_URL } from './api';
import { authService } from './auth';

export interface PaymentCreateRequest {
  amount: number;
  currency?: string;
  description?: string;
  product_name?: string;
  return_url: string;
  cancel_url: string;
}

export interface PaymentApprovalResponse {
  payment_id: string;
  approval_url: string;
  status: string;
}

export interface PaymentExecuteRequest {
  payment_id: string;
  payer_id: string;
}

export interface Payment {
  id: number;
  user_id: number;
  paypal_payment_id: string | null;
  amount: number;
  currency: string;
  status: string;
  description: string | null;
  product_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentStatusResponse {
  payment_id: number;
  paypal_payment_id: string | null;
  status: string;
  amount: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

class PaymentService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE_URL}/api/payments`;
  }

  private getHeaders(): HeadersInit {
    const token = authService.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  async createPayment(paymentData: PaymentCreateRequest): Promise<PaymentApprovalResponse> {
    const response = await fetch(`${this.baseUrl}/create`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(paymentData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create payment');
    }

    return response.json();
  }

  async executePayment(executeData: PaymentExecuteRequest): Promise<Payment> {
    const response = await fetch(`${this.baseUrl}/execute`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(executeData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to execute payment');
    }

    return response.json();
  }

  async getPaymentHistory(): Promise<Payment[]> {
    const response = await fetch(`${this.baseUrl}/history`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get payment history');
    }

    return response.json();
  }

  async getPaymentStatus(paymentId: number): Promise<PaymentStatusResponse> {
    const response = await fetch(`${this.baseUrl}/${paymentId}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get payment status');
    }

    return response.json();
  }

  async cancelPayment(paymentId: number): Promise<PaymentStatusResponse> {
    const response = await fetch(`${this.baseUrl}/${paymentId}/cancel`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to cancel payment');
    }

    return response.json();
  }

  /**
   * Initiate PayPal checkout flow
   * @param amount Payment amount
   * @param productName Name of product/service
   * @param description Payment description
   * @returns Approval URL to redirect user to PayPal
   */
  async initiatePayPalCheckout(
    amount: number,
    productName: string,
    description?: string
  ): Promise<string> {
    const returnUrl = `${window.location.origin}/payment/success`;
    const cancelUrl = `${window.location.origin}/payment/cancel`;

    const paymentData: PaymentCreateRequest = {
      amount,
      currency: 'USD',
      product_name: productName,
      description,
      return_url: returnUrl,
      cancel_url: cancelUrl,
    };

    const response = await this.createPayment(paymentData);
    return response.approval_url;
  }

  /**
   * Complete payment after user returns from PayPal
   * @param paymentId PayPal payment ID from URL params
   * @param payerId PayPal payer ID from URL params
   */
  async completePayment(paymentId: string, payerId: string): Promise<Payment> {
    return this.executePayment({
      payment_id: paymentId,
      payer_id: payerId,
    });
  }
}

export const paymentService = new PaymentService();
